import * as React from 'react';
import NextLink from 'next/link';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import axios from '../../utils/axios';
import { HistoricoCampoDataProps, HistoricoGPS } from 'components/historicoGPS';
import { Num, BotaoExportar } from './index';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from './Modal';
import ModalPassos, { PassoModal } from './ModalPassos';
import { AutoCompleteMultiplo, AutoCompleteUnico, buscarPaginado } from './AutoComplete';
import ModalAplicativoPolitica, {
  AplicativoPolitica,
  DESCRICAO_TIPO,
  MODOS_ATUALIZACAO,
  TIPOS_INSTALACAO,
  rotuloAtualizacao,
  rotuloTipo,
} from './ModalAplicativoPolitica';

/* ============================================================================
   Politica - formulario, visual novo (shadcn + Tailwind).

   Substitui `politicas/new/[name].tsx` (2.496 linhas, MUI + react-hook-form +
   Tabs + DataGrid + Swal). Rota PARALELA de proposito: esta e a unica tela do
   sistema em que um nome de campo errado nao da erro nenhum na interface - grava,
   sobe para `policies.patch` do Google e muda o comportamento de aparelho em
   campo. Com as duas rotas vivas da para comparar o corpo enviado pelas duas
   versoes antes de trocar o link do menu.

   ------------------------------------------------------------------ ESTRUTURA
   Cartoes, nao abas. Uma politica tem 51 campos e quatro assuntos distintos
   (identificacao, aplicativos, quiosque, bloqueios); em abas o usuario nao ve o
   que mudou fora da aba aberta, e o botao Salvar manda tudo de qualquer forma.
   Com cartoes numa coluna, mais a barra de secoes fixa no topo, o formulario
   inteiro e um documento: da para rolar, achar, e conferir antes de salvar.

   ------------------------------------------------------- O QUE O BACKEND ESPERA
   POST /politicas            -> criar
   PUT  /politicas/:name      -> atualizar
   GET  /politicas/byId/:name -> carregar
   GET  /politicas/sites/:name-> lista de sites bloqueados

   O corpo enviado esta em `montarCorpo()` e tem as MESMAS 54 chaves da tela
   antiga, com as mesmas conversoes (bluetoothDisabled/bloqueioSenha/configTela
   viram booleano a partir de 0|1). Onze delas nao tem controle nenhum na tela -
   sao carregadas da politica e reenviadas iguais (a lista esta em `SEM_CONTROLE`).
   Elas ja tiveram uma aba propria de leitura aqui; foi retirada a pedido - era uma
   aba de diagnostico no meio de um cadastro.

   ------------------------------------------------- DEFEITOS QUE FORAM CORRIGIDOS
   Todos verificados no arquivo antigo, linha por linha:

   1. NAO ERA POSSIVEL CRIAR NEM DUPLICAR POLITICA. O gate
      `if (listCampoData.length == 0) return` roda tambem na criacao, e a lista de
      diferencas so e preenchida quando `oldEntity.name` existe. Criando do zero
      ela fica vazia, entao a tela respondia "Nao houve alteracoes na politica" e
      nao enviava nada. Aqui o gate vale SO na edicao.

   2. DUPLICAR SOBRESCREVIA A ORIGEM. O carregamento tira o `name` da copia
      (comentario explicito na linha 411 do arquivo antigo), e 130 linhas depois
      `setOldEntity({ ...response.data, sitesBloqueios: siteData.data })` devolve o
      `name` da origem. Salvar a "copia" viraria PUT na politica original. Estava
      mascarado pelo defeito 3.

   3. A COPIA NAO LEVAVA OS SITES BLOQUEADOS: a busca de sites usava `name` (que
      na copia vale '0') em vez da politica de origem.

   4. MUDAR SO "Redes moveis(roaming)" ERA DESCARTADO. `dataRoamingDisabled` tem
      checkbox mas nao entrava na lista de diferencas; sozinho, deixava a lista
      vazia e caia no gate do item 1. O usuario via "Nao houve alteracoes".

   5. Comparacoes de booleano com `!=` entre `undefined` e `false` geravam linha de
      historico para campo que ninguem tocou (coluna NULL no banco). Normalizado
      com `!!` nos dois lados.

   6. Historico com rotulo/valor errado: `bluetoothDisabled` era registrado como
      "Ajuste de volume"; `hub` gravava o valor NOVO nos dois lados; `barraStatus`
      gravava novo vazio quando o antigo era 0; `status` (0|1) era descrito como
      "Marcado/Nao Marcado".

   7. Exportar da aba Aplicativos baixava a lista de aplicativos DISPONIVEIS com o
      nome de arquivo "Aplicativos Adicionados".

   8. `debugger` em producao (duas ocorrencias) e `console.log` por render no modal.

   Um defeito de BACKEND que esta tela nao resolve e que aparece ao testar:
   `policies.patch` e chamado sem `updateMask`, entao os campos de Schema$Policy
   que nao enviamos voltam ao default do Google a cada save. Detalhe em
   PENDENCIAS secao 2.2.
   ============================================================================ */

const KIOSK_PADRAO_PACKAGE = 'br.com.gps.gpshub';
const KIOSK_PADRAO_NAME = 'GPShub';
const ITEM_PADRAO_KIOSK = { valor: KIOSK_PADRAO_PACKAGE, rotulo: KIOSK_PADRAO_NAME };

type Opcao = { valor: string | number; rotulo: string };

/* Rede Wi-Fi EXTRA da politica: so SOMA as do aparelho (ver
   remoteAPI/src/app/politicas/wifi/redes-wifi.ts). A senha nunca volta da API:
   `temSenha` diz se ha uma guardada, e senha vazia na edicao = manter. */
type RedeWifi = {
  chave: string;
  codigo?: number;
  ssid: string;
  seguranca: 'WPA-PSK' | 'WEP-PSK' | 'None';
  senha: string;
  oculta: boolean;
  temSenha?: boolean;
};

const MAX_REDES_WIFI = 10;

function redeWifiValida(r: RedeWifi): boolean {
  if (!r.ssid.trim()) return false;
  if (r.seguranca === 'None') return true;
  if (!r.senha && r.temSenha) return true; // mantem a guardada
  if (r.seguranca === 'WPA-PSK') return r.senha.length >= 8 && r.senha.length <= 63;
  return [5, 13, 10, 26].includes(r.senha.length);
}

type Site = {
  codigo?: number;
  site?: string;
  tipo?: string;
  todos?: string;
  novo?: boolean;
};

// ---------------------------------------------------------------------------
// Listas de opcoes. Valores conferidos contra politicas.entity.ts e contra o
// que a tela antiga enviava - nenhum valor foi inventado nem "corrigido".
// ---------------------------------------------------------------------------
const OPCOES = {
  status: [
    { valor: 1, rotulo: 'ATIVO' },
    { valor: 0, rotulo: 'INATIVO' },
  ] as Opcao[],
  hub: [
    { valor: 'KIOSK', rotulo: 'BLOQUEADO' },
    { valor: 'REQUIRED_FOR_SETUP', rotulo: 'DESBLOQUEADO' },
  ] as Opcao[],
  barraStatus: [
    { valor: 3, rotulo: 'NOTIFICAÇÃO E NAVEGACÃO ATIVA' },
    { valor: 0, rotulo: 'NOTIFICAÇÃO E NAVEGACÃO DESATIVADA' },
    { valor: 1, rotulo: 'NOTIFICAÇÃO ATIVA' },
    { valor: 2, rotulo: 'NAVEGAÇÃO ATIVA' },
  ] as Opcao[],
  playStoreMode: [
    { valor: 'WHITELIST', rotulo: 'BLOQUEADO' },
    { valor: 'BLACKLIST', rotulo: 'DESBLOQUEADO' },
  ] as Opcao[],
  airplaneModeState: [
    { valor: 'AIRPLANE_MODE_DISABLED', rotulo: 'DESATIVADO' },
    { valor: 'AIRPLANE_MODE_USER_CHOICE', rotulo: 'ESCOLHA DO USUARIO' },
  ] as Opcao[],
  locationMode: [
    { valor: 'LOCATION_DISABLED', rotulo: 'DESATIVADO' },
    { valor: 'LOCATION_ENFORCED', rotulo: 'ATIVADO' },
    { valor: 'LOCATION_USER_CHOICE', rotulo: 'ESCOLHA DO USUARIO' },
  ] as Opcao[],
  cameraAccess: [
    { valor: 'CAMERA_ACCESS_DISABLED', rotulo: 'DESATIVADO' },
    { valor: 'CAMERA_ACCESS_ENFORCED', rotulo: 'ATIVADO' },
    { valor: 'CAMERA_ACCESS_USER_CHOICE', rotulo: 'ESCOLHA DO USUARIO' },
  ] as Opcao[],
  untrustedAppsPolicy: [
    { valor: 'DISALLOW_INSTALL', rotulo: 'DESATIVADO' },
    { valor: 'ALLOW_INSTALL_DEVICE_WIDE', rotulo: 'ATIVADO' },
  ] as Opcao[],
  tetheringSettings: [
    { valor: 'DISALLOW_ALL_TETHERING', rotulo: 'BLOQUEADO TODOS' },
    { valor: 'DISALLOW_WIFI_TETHERING', rotulo: 'BLOQUEADO WIFI ROTEADOR' },
    { valor: 'ALLOW_ALL_TETHERING', rotulo: 'PERMITIR TODOS' },
  ] as Opcao[],
  bluetoothDisabled: [
    { valor: 0, rotulo: 'ATIVADO' },
    { valor: 1, rotulo: 'DESATIVADO' },
  ] as Opcao[],
  systemUpdate: [
    { valor: 'SYSTEM_UPDATE_TYPE_UNSPECIFIED', rotulo: 'Escolha do usuario' },
    { valor: 'WINDOWED', rotulo: 'Padrão' },
    { valor: 'AUTOMATIC', rotulo: 'Automatica' },
    { valor: 'POSTPONE', rotulo: 'Atualização 30 dias depois' },
  ] as Opcao[],
  developerSettings: [
    { valor: 'DEVELOPER_SETTINGS_DISABLED', rotulo: 'DESATIVADO' },
    { valor: 'DEVELOPER_SETTINGS_ALLOWED', rotulo: 'ATIVADO' },
  ] as Opcao[],
  usbDataAccess: [
    { valor: 'DISALLOW_USB_DATA_TRANSFER', rotulo: 'TODOS BLOQUEADOS' },
    { valor: 'DISALLOW_USB_FILE_TRANSFER', rotulo: 'TRANSFERÊNCIA DADOS INATIVO E MOUSE/TECLADO ATIVO' },
    { valor: 'ALLOW_USB_DATA_TRANSFER', rotulo: 'TODOS LIBERADOS' },
  ] as Opcao[],
  crossProfileCopyPaste: [
    { valor: 'CROSS_PROFILE_COPY_PASTE_UNSPECIFIED', rotulo: 'NÃO ESPECIFICADO' },
    { valor: 'COPY_FROM_WORK_TO_PERSONAL_DISALLOWED', rotulo: 'TRABALHO → PESSOAL BLOQUEADO' },
    { valor: 'CROSS_PROFILE_COPY_PASTE_ALLOWED', rotulo: 'PERMITIDO ENTRE PERFIS (PADRÃO)' },
  ] as Opcao[],
  crossProfileDataSharing: [
    { valor: 'CROSS_PROFILE_DATA_SHARING_UNSPECIFIED', rotulo: 'NÃO ESPECIFICADO' },
    { valor: 'CROSS_PROFILE_DATA_SHARING_DISALLOWED', rotulo: 'BLOQUEADO (AMBAS DIREÇÕES)' },
    { valor: 'DATA_SHARING_FROM_WORK_TO_PERSONAL_DISALLOWED', rotulo: 'TRABALHO → PESSOAL BLOQUEADO' },
    { valor: 'CROSS_PROFILE_DATA_SHARING_ALLOWED', rotulo: 'PERMITIDO ENTRE PERFIS (PADRÃO)' },
  ] as Opcao[],
  bloqueioSenha: [
    { valor: 0, rotulo: 'DESATIVADO' },
    { valor: 1, rotulo: 'ATIVO' },
  ] as Opcao[],
  autoDateAndTimeZone: [
    { valor: 'AUTO_DATE_AND_TIME_ZONE_USER_CHOICE', rotulo: 'ESCOLHA USUÁRIO' },
    { valor: 'AUTO_DATE_AND_TIME_ZONE_ENFORCED', rotulo: 'AUTOMÁTICO' },
  ] as Opcao[],
  configTela: [
    { valor: 0, rotulo: 'DESATIVADO' },
    { valor: 1, rotulo: 'ATIVO' },
  ] as Opcao[],
};

/** Dicas do Google, iguais as da tela antiga (eram `labelIcon` do RHFSelect). */
const DICAS: Record<string, string> = {
  hub: 'Bloqueado é quando a UI do Android fica em modo Kiosk; Desbloqueado volta a ser a tela padrão do Android.',
  barraStatus: 'Padrão é tudo bloqueado; os outros tipos são sugestivos à escolha feita.',
  playStoreMode:
    'Bloqueado: somente a lista de aplicativos instalados. Desbloqueado: todos os aplicativos da PlayStore.',
  airplaneModeState: 'Controla o estado do modo avião e se o usuário pode ativá-lo ou desativá-lo.',
  locationMode: 'Localização no dispositivo.',
  cameraAccess: 'Câmera do dispositivo.',
  untrustedAppsPolicy: 'Instalar outros aplicativos e APKs de fontes desconhecidas.',
  tetheringSettings:
    'Controla até que ponto o usuário pode usar formas de tethering: Wi-Fi, tethering Bluetooth etc.',
  bluetoothDisabled: 'Pode ligar ou desligar o Bluetooth.',
  systemUpdate:
    'Controla a atualização do sistema, versão do Android e patch de segurança. Padrão: atualiza automaticamente, mas só entre 02:00 e 04:00. Escolha do usuário: comportamento padrão do aparelho, normalmente exigindo que o usuário aceite. Automática: instala assim que a atualização estiver disponível. 30 dias depois: instala após 30 dias de disponibilidade.',
  developerSettings:
    'DESATIVADO: desativa todas as configurações do desenvolvedor e impede o usuário de acessá-las. ATIVADO: o usuário pode acessar e definir as configurações.',
  usbDataAccess:
    'TODOS BLOQUEADOS: nenhum tipo de transferência de dados por USB é permitido. TRANSFERÊNCIA DADOS INATIVO E MOUSE/TECLADO ATIVO: arquivos por USB não, mas mouse e teclado sim. TODOS LIBERADOS: qualquer transferência por USB.',
  crossProfileCopyPaste:
    'Controla se o texto copiado em um perfil (pessoal ou de trabalho) pode ser colado no outro. NÃO ESPECIFICADO equivale a TRABALHO → PESSOAL BLOQUEADO.',
  crossProfileDataSharing:
    'Controla se dados de um perfil podem ser compartilhados com apps do outro via intents (abrir navegador, mapa, compartilhar conteúdo, abrir documento). NÃO ESPECIFICADO equivale a TRABALHO → PESSOAL BLOQUEADO.',
  bloqueioSenha: 'Bloqueia a tela do dispositivo com uma senha, que será a chave de acesso.',
  autoDateAndTimeZone: 'Se data, hora e fuso horário automáticos ficam ativados no aparelho da empresa.',
  configTela:
    'Na configuração do aplicativo GPShub fica disponível a opção de exibir brilho e informações da tela.',
  kiosk: 'Aplicativo que fica em modo quiosque. Trocar o app devolve o anterior para "Forçar instalação".',
};

/**
 * Checkboxes do bloco "Desabilitar Configurações Padrões", na ordem e com os
 * rotulos e tooltips da tela antiga. Os que la estavam comentados NAO entram -
 * seus valores continuam sendo enviados, so nao ganham controle novo (a lista
 * esta em `SEM_CONTROLE`).
 */
const CAIXAS_PADRAO: Array<{ chave: string; rotulo: string; dica: string }> = [
  { chave: 'bluetoothConfigDisabled', rotulo: 'Bluetooth (configuração)', dica: 'Desativa a alteração do Bluetooth.' },
  { chave: 'screenCaptureDisabled', rotulo: 'Captura de tela', dica: 'A captura de tela do aparelho fica desativada.' },
  // Pela documentacao, alem de travar o volume TAMBEM SILENCIA o aparelho - a dica antiga so falava do volume.
  { chave: 'adjustVolumeDisabled', rotulo: 'Sem som (bloqueia o volume)', dica: 'Silencia o aparelho e impede ajustar o volume. Só vale em aparelho totalmente gerenciado.' },
  { chave: 'wifiConfigDisabled', rotulo: 'WIFI', dica: 'Desativa a alteração do WIFI.' },
  { chave: 'modifyAccountsDisabled', rotulo: 'Gerenciamento de contas', dica: 'Inativa o gerenciamento de contas.' },
  {
    chave: 'uninstallAppsDisabled',
    rotulo: 'Desistalar aplicativos',
    dica: 'Desativa desinstalar programas de maneira geral, inclusive os cadastrados aqui.',
  },
  {
    chave: 'installAppsDisabled',
    rotulo: 'Adicionar aplicativos',
    dica: 'O usuário não poderá instalar aplicativos, mesmo os disponíveis para download.',
  },
  {
    chave: 'bluetoothContactSharingDisabled',
    rotulo: 'Compartilhar contatos via Bluetooth',
    dica: 'Inativa o compartilhamento de contatos por Bluetooth.',
  },
  {
    /* ESTE CAMPO NAO CHEGA AO APARELHO (29/09/2026).

       `smsDisabled` e gravado em TAB_POLITICA e aparece no historico, mas NAO
       existe no corpo que `createPolicy.ts` envia ao Google - conferido no
       arquivo inteiro. A politica e salva, a tela mostra "bloqueado", e o
       aparelho continua mandando SMS.

       MEDIDO NO BANCO: 1.975 das 4.992 politicas estao com ele ligado. Ou seja,
       quase dois mil grupos acreditam bloquear SMS e nao bloqueiam - e como o
       campo vinha MARCADO POR PADRAO, o numero so crescia.

       POR QUE NAO FIZ ELE FUNCIONAR. Passar a enviar `smsDisabled` bloquearia
       SMS de verdade nesses 1.975 grupos de uma vez, sem ninguem ter pedido.
       Isso e decisao de produto, nao correcao de defeito, e precisa ser tomada
       sabendo quantos aparelhos muda.

       Ate la, o rotulo diz a verdade. Mentir sobre uma protecao que nao existe
       e pior do que nao ter a protecao: alguem confere a tela, ve "bloqueado" e
       para de procurar. */
    chave: 'smsDisabled',
    rotulo: 'SMS (não aplicado)',
    dica: 'Fica gravado na política, mas NÃO é enviado ao aparelho hoje — o SMS continua funcionando. Para valer, precisa de ajuste no envio ao Google.',
  },
  {
    /* ROTULO CORRIGIDO (29/09/2026).

       Era so "Dados móveis", ao lado de "Redes móveis (roaming)", que DESLIGA
       o servico. A leitura natural da dupla e "aqui desligo os dados, ali
       desligo o roaming" - e nao e isso: `mobileNetworksConfigDisabled` apenas
       impede o usuario de MEXER na configuracao de rede movel. Os dados
       continuam funcionando.

       A dica ja dizia a verdade, mas rotulo curto e o que se le na varredura
       da lista; quem marcasse esperando cortar o consumo de dados ficaria com
       o consumo intacto e sem saber por que. */
    chave: 'mobileNetworksConfigDisabled',
    rotulo: 'Bloquear config. de rede móvel',
    dica: 'Impede o usuário de alterar a configuração de redes móveis. NÃO desliga os dados.',
  },
  { chave: 'dataRoamingDisabled', rotulo: 'Redes móveis (roaming)', dica: 'Desativa serviços de dados em roaming.' },
];

/**
 * Campos que VAO no corpo do POST/PUT e nao tem controle em tela nenhuma - nem na
 * antiga. Aparecem somente-leitura para que ninguem descubra por acidente que a
 * politica manda `addUserDisabled: true` para o aparelho.
 */
const SEM_CONTROLE: Array<{ chave: string; rotulo: string }> = [
  { chave: 'defaultPermissionPolicy', rotulo: 'Permissão padrão dos apps' },
  { chave: 'networkResetDisabled', rotulo: 'Restauração de rede desativada' },
  { chave: 'addUserDisabled', rotulo: 'Novos usuários no perfil desativado' },
  { chave: 'mountPhysicalMediaDisabled', rotulo: 'Mídia externa física desativada' },
  { chave: 'credentialsConfigDisabled', rotulo: 'Configuração de credenciais desativada' },
  { chave: 'vpnConfigDisabled', rotulo: 'Configuração de VPN desativada' },
  { chave: 'createWindowsDisabled', rotulo: 'Criação de janelas desativada' },
  { chave: 'outgoingBeamDisabled', rotulo: 'NFC para enviar dados desativado' },
  { chave: 'outgoingCallsDisabled', rotulo: 'Chamadas de saída desativadas' },
  { chave: 'removeUserDisabled', rotulo: 'Remoção de outros usuários desativada' },
  { chave: 'configRoteador', rotulo: 'Configuração de roteador' },
];

const PADRAO: Record<string, any> = {
  nome: '',
  entidade: null,
  status: 1,
  hub: 'KIOSK',
  defaultPermissionPolicy: 'GRANT',
  bluetoothConfigDisabled: true,
  barraStatus: 3,
  powerButtonActions: true,
  addUserDisabled: true,
  deviceSettings: true,
  networkResetDisabled: true,
  factoryResetDisabled: true,
  modifyAccountsDisabled: true,
  bluetoothContactSharingDisabled: true,
  screenCaptureDisabled: true,
  /* Deixou de nascer MARCADO (29/09/2026): ver o comentario do campo. Enquanto
     ele nao for enviado ao Google, marcar por padrao so aumenta o numero de
     politicas que acreditam bloquear SMS e nao bloqueiam. */
  smsDisabled: false,
  playStoreMode: 'WHITELIST',
  airplaneModeState: 'AIRPLANE_MODE_DISABLED',
  locationMode: 'LOCATION_ENFORCED',
  cameraAccess: 'CAMERA_ACCESS_ENFORCED',
  untrustedAppsPolicy: 'ALLOW_INSTALL_DEVICE_WIDE',
  tetheringSettings: 'DISALLOW_ALL_TETHERING',
  bluetoothDisabled: 1,
  frpAdminEmails: '',
  systemUpdate: 'WINDOWED',
  developerSettings: 'DEVELOPER_SETTINGS_DISABLED',
  usbDataAccess: 'DISALLOW_USB_DATA_TRANSFER',
  bloqueioSenha: 0,
  autoDateAndTimeZone: 'AUTO_DATE_AND_TIME_ZONE_ENFORCED',
  configTela: 0,
  configRoteador: false,
  crObrigatorio: false,
  cpfObrigatorio: false,
  crossProfileCopyPaste: 'CROSS_PROFILE_COPY_PASTE_ALLOWED',
  crossProfileDataSharing: 'CROSS_PROFILE_DATA_SHARING_ALLOWED',
  // Sem controle na tela antiga nem aqui; ficam com o padrao do banco (falso) e
  // sao sobrescritos pelo valor carregado da politica.
  adjustVolumeDisabled: false,
  wifiConfigDisabled: false,
  dataRoamingDisabled: false,
  installAppsDisabled: false,
  uninstallAppsDisabled: false,
  mountPhysicalMediaDisabled: false,
  credentialsConfigDisabled: false,
  vpnConfigDisabled: false,
  mobileNetworksConfigDisabled: false,
  createWindowsDisabled: false,
  outgoingBeamDisabled: false,
  outgoingCallsDisabled: false,
  removeUserDisabled: false,
};


// ---------------------------------------------------------------------------
// Pecas de formulario. Locais porque so esta tela precisa de select/checkbox com
// dica longa; se uma segunda tela precisar, sobem para components/gps.
// ---------------------------------------------------------------------------

function Selecao({
  rotulo,
  dica,
  valor,
  opcoes,
  onChange,
  desabilitado,
}: {
  rotulo: string;
  dica?: string;
  valor: any;
  opcoes: Opcao[];
  onChange: (valor: any) => void;
  desabilitado?: boolean;
}) {
  // O <select> devolve string sempre. Quando as opcoes sao numericas (status,
  // barraStatus, bluetoothDisabled, bloqueioSenha, configTela) precisamos devolver
  // NUMERO, senao `bluetoothDisabled == 1 ? true : false` no corpo compararia
  // "1" com 1 e mandaria o booleano errado para o aparelho.
  const numerico = opcoes.length > 0 && typeof opcoes[0].valor === 'number';

  return (
    <Campo rotulo={rotulo} dica={dica}>
      <select
        className={classesEntrada}
        value={String(valor ?? '')}
        disabled={desabilitado}
        onChange={(e) => onChange(numerico ? Number(e.target.value) : e.target.value)}
      >
        {opcoes.map((o) => (
          <option key={String(o.valor)} value={String(o.valor)}>
            {o.rotulo}
          </option>
        ))}
      </select>
    </Campo>
  );
}

function Caixa({
  rotulo,
  dica,
  marcado,
  onChange,
}: {
  rotulo: string;
  dica?: string;
  marcado: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-line p-2.5 hover:bg-soft">
      <input
        type="checkbox"
        checked={!!marcado}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 flex-none accent-[hsl(var(--primary))]"
      />
      <span className="min-w-0">
        <span className="block text-[12px] font-semibold leading-tight">{rotulo}</span>
        {dica && <span className="mt-0.5 block text-[11px] leading-snug text-faint">{dica}</span>}
      </span>
    </label>
  );
}

/**
 * Seletor de grupo com busca no servidor.
 *
 * URL identica a que o RHFAutoComplete montava na tela antiga:
 *   /entidades/paginado?page=<p>&rows=<n>&valor=<busca>&campo=nome&somente=<cod>&politicas=true
 * (era page=0&rows=50; desde 21/10/2026 e paginado e so abre na lupa)
 *
 * `politicas=true` faz o backend devolver so grupos SEM politica - e o que
 * permite criar. Detalhe do backend que vale saber ao usar: em
 * entidades.service.ts:111-116 o `where` recebe um ARRAY, que no TypeORM e OR;
 * entao com texto digitado a busca por nome tambem traz grupos que JA tem
 * politica. Comportamento antigo, mantido igual - salvar num grupo que ja tem
 * politica e recusado pelo backend.
 */
function SeletorGrupo({
  valor,
  somente,
  desabilitado,
  onChange,
}: {
  valor: any;
  somente?: number | string;
  desabilitado?: boolean;
  onChange: (grupo: any) => void;
}) {
  /* Lupa + modal paginado (21/10/2026): antes eram 50 grupos ao focar o campo
     e mais 50 a cada pausa na digitacao. Mesma URL, so com page/rows. */
  const buscarGrupos = React.useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/entidades/paginado?page=${pagina}&rows=${linhas}&valor=${encodeURIComponent(termo)}&campo=nome` +
          `&somente=${somente ?? ''}&politicas=true`,
        (g) => ({ codigo: g.codigo, rotulo: g.nome, secundario: g.regional?.nome || 'sem regional', dado: g })
      ),
    [somente]
  );

  if (desabilitado) {
    return (
      <Campo rotulo="Grupo" obrigatorio>
        <div className="flex h-9 items-center rounded-lg border border-line bg-soft px-3 text-[12.5px] text-muted-foreground">
          {valor?.nome || '—'}
        </div>
      </Campo>
    );
  }

  // Sem `Campo` (<label>) aqui: os botoes "trocar"/"remover" ficam dentro dele.
  return (
    <div className="block">
      <span className="mb-1 block text-[12px] font-semibold">
        Grupo<span className="ml-1 text-danger">*</span>
      </span>
      <AutoCompleteUnico
        selecionado={
          valor?.codigo
            ? { codigo: valor.codigo, rotulo: valor.nome, secundario: valor.regional?.nome || 'sem regional' }
            : null
        }
        // O registro inteiro vai em `entidade`, como antes.
        aoEscolher={(o) => onChange(o ? o.dado : null)}
        buscar={buscarGrupos}
        titulo="Selecionar grupo"
        placeholder="Nome do grupo (Enter ou lupa)"
        vazio="Nenhum grupo sem política encontrado."
      />
      <span className="mt-1 block text-[11px] text-faint">
        Só aparecem grupos que ainda não têm política.
      </span>
    </div>
  );
}

/* ----------------------------------------------------------------------------
   DE PAGINA PARA MODAL (18/08/2026).

   Este formulario era a pagina `politicas-novo/new/[name]`. Virou modal em abas
   por decisao do dono do produto, e a troca so ficou razoavel DEPOIS de o
   ModalPassos passar de cartoes para abas: 51 campos em 8 blocos nao caberiam num
   modal de cartoes lado a lado.

   O QUE NAO MUDOU - e a parte que importa: o carregamento, `montarDiferencas()`,
   `montarCorpo()` e o `salvar()` estao IDENTICOS ao que foi commitado em c28f699.
   Esta e a unica tela do sistema em que um campo trocado nao da erro na interface:
   grava, sobe para `policies.patch` do Google e muda o comportamento de aparelho em
   campo. Trocar a moldura e uma coisa; mexer no corpo enviado seria outra, e nao
   foi feita.

   `nome` e `copiarDe` viram PROPS em vez de sair do router, para o modal poder ser
   aberto direto da lista. A rota antiga continua como casca fina
   (pages/dashboard/politicas-novo/new/[name].tsx), entao link e favorito antigos
   seguem funcionando.
   ---------------------------------------------------------------------------- */
export default function ModalPolitica({
  aberto,
  nome: nomeProp,
  copiarDe: copiarDeProp,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  /** `name` da politica a editar. Vazio ou '0' = criar. */
  nome?: string;
  /** `name` da politica de origem, para duplicar. */
  copiarDe?: string;
  onFechar: () => void;
  /** Chamado depois de salvar, com o `name` resultante. */
  onSalvo?: (name: string) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const name = nomeProp || '';
  const copiarDe = copiarDeProp || '';
  const ehCopia = Boolean(copiarDe) && (!name || name === '0');
  const editando = Boolean(name && name !== '0') && !ehCopia;

  const [form, setForm] = React.useState<Record<string, any>>({ ...PADRAO });
  const [original, setOriginal] = React.useState<any>({});
  const [carregando, setCarregando] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);

  // aplicativos = vinculados a politica; selecionados = escolhidos para adicionar
  const [aplicativos, setAplicativos] = React.useState<AplicativoPolitica[]>([]);
  const [selecionados, setSelecionados] = React.useState<any[]>([]);
  const [tipoNovo, setTipoNovo] = React.useState('FORCE_INSTALLED');
  const [atualizacaoNova, setAtualizacaoNova] = React.useState('AUTO_UPDATE_DEFAULT');

  const [campoFiltro, setCampoFiltro] = React.useState('name');
  const [buscaVinculados, setBuscaVinculados] = React.useState('');
  const [editandoApp, setEditandoApp] = React.useState<AplicativoPolitica | null>(null);

  const [listaKiosk, setListaKiosk] = React.useState<Opcao[]>([ITEM_PADRAO_KIOSK]);
  const [kiosk, setKiosk] = React.useState<Opcao>(ITEM_PADRAO_KIOSK);

  const [sites, setSites] = React.useState<Site[]>([]);
  const [redesWifi, setRedesWifi] = React.useState<RedeWifi[]>([]);
  const [siteTodos, setSiteTodos] = React.useState('Não');
  const [siteTexto, setSiteTexto] = React.useState('');
  const [siteTipo, setSiteTipo] = React.useState('Bloqueio');

  const [confirmarApps, setConfirmarApps] = React.useState(false);
  const [exportando, setExportando] = React.useState(false);

  const podeSalvar = Number(user?.perfil) !== 0;

  function set(chave: string, valor: any) {
    setForm((f) => ({ ...f, [chave]: valor }));
  }

  /** Reconstroi a lista do seletor de Kiosk a partir dos aplicativos vinculados. */
  function recalcularListaKiosk(lista: AplicativoPolitica[]) {
    const nova: Opcao[] = [ITEM_PADRAO_KIOSK];
    lista.forEach((a) => nova.push({ rotulo: a.name || String(a.packageName), valor: String(a.packageName) }));
    setListaKiosk(nova);
    return nova;
  }

  // -------------------------------------------------------------- carregamento
  const carregar = React.useCallback(async () => {
    const origem = ehCopia ? copiarDe : name;
    if (!origem || origem === '0') return;

    setCarregando(true);
    try {
      const { data } = await axios.get(`/politicas/byId/${encodeURIComponent(origem)}`);
      if (!data?.name) return;

      // Na copia o `name` NUNCA entra: o backend decide criar ou atualizar por ele,
      // e com o name da origem preenchido salvar a copia sobrescreveria a original.
      /* A copia tambem NAO herda o grupo (30/09/2026). Um grupo so tem uma
         politica - e a copia com o grupo da origem foi exatamente o que deixou o
         Shopee-MA-PI com duas. Com a trava no servidor, herdar o grupo tornava a
         copia impossivel de salvar (e o seletor ficava travado). Sem grupo, o
         seletor abre para escolher um grupo que ainda nao tenha politica. */
      const base = ehCopia ? { ...data, name: '', tokenGoogle: '', entidade: null } : data;

      const carregado: Record<string, any> = { ...PADRAO };
      Object.keys(PADRAO).forEach((k) => {
        if (data[k] !== null && data[k] !== undefined) carregado[k] = data[k];
      });

      carregado.nome = ehCopia ? `Copia de ${data.nome || ''}` : data.nome || '';
      carregado.entidade = ehCopia ? null : data.entidade || null;
      carregado.status = data.status ?? 1;
      carregado.hub = data.hub || 'KIOSK';
      carregado.defaultPermissionPolicy = data.defaultPermissionPolicy || 'GRANT';
      // 0|1 na tela, booleano no corpo - igual a tela antiga.
      carregado.bluetoothDisabled = data.bluetoothDisabled ? 1 : 0;
      carregado.bloqueioSenha = data.bloqueioSenha ? 1 : 0;
      carregado.configTela = data.configTela ? 1 : 0;
      carregado.barraStatus = data.barraStatus == null ? 0 : data.barraStatus;
      carregado.airplaneModeState = data.airplaneModeState || 'AIRPLANE_MODE_DISABLED';
      carregado.locationMode = data.locationMode || 'LOCATION_ENFORCED';
      carregado.cameraAccess = data.cameraAccess || 'CAMERA_ACCESS_ENFORCED';
      carregado.untrustedAppsPolicy = data.untrustedAppsPolicy || 'ALLOW_INSTALL_DEVICE_WIDE';
      carregado.tetheringSettings = data.tetheringSettings || 'DISALLOW_ALL_TETHERING';
      carregado.systemUpdate = data.systemUpdate || 'WINDOWED';
      carregado.autoDateAndTimeZone = data.autoDateAndTimeZone || 'AUTO_DATE_AND_TIME_ZONE_ENFORCED';
      carregado.developerSettings = data.developerSettings || 'DEVELOPER_SETTINGS_DISABLED';
      carregado.usbDataAccess = data.usbDataAccess || 'DISALLOW_USB_DATA_TRANSFER';
      carregado.playStoreMode = data.playStoreMode || 'WHITELIST';
      carregado.crossProfileCopyPaste = data.crossProfileCopyPaste || 'CROSS_PROFILE_COPY_PASTE_ALLOWED';
      carregado.crossProfileDataSharing = data.crossProfileDataSharing || 'CROSS_PROFILE_DATA_SHARING_ALLOWED';
      setForm(carregado);

      const apps: AplicativoPolitica[] = data.aplicativos || [];
      setAplicativos(apps);
      const nova = recalcularListaKiosk(apps);
      const achado = nova.find((k) => k.valor === data.kiosk);
      setKiosk(achado || ITEM_PADRAO_KIOSK);

      // Sites: a tela antiga buscava por `name`, que na copia vale '0' - por isso a
      // copia perdia os sites bloqueados. Aqui a busca usa a ORIGEM.
      let sitesCarregados: Site[] = [];
      try {
        const r = await axios.get(`/politicas/sites/${encodeURIComponent(origem)}`);
        sitesCarregados = Array.isArray(r.data) ? r.data : [];
      } catch {
        sitesCarregados = [];
      }
      setSites(sitesCarregados);

      // Redes Wi-Fi: a copia NAO leva (as senhas nao voltam da API).
      let redesCarregadas: RedeWifi[] = [];
      if (!ehCopia) {
        try {
          const r = await axios.get(`/politicas/wifi/${encodeURIComponent(origem)}`);
          redesCarregadas = (Array.isArray(r.data) ? r.data : []).map((x: any) => ({
            chave: `w${x.codigo}`,
            codigo: x.codigo,
            ssid: x.ssid ?? '',
            seguranca: x.seguranca ?? 'WPA-PSK',
            senha: '',
            oculta: Boolean(x.oculta),
            temSenha: Boolean(x.temSenha),
          }));
        } catch {
          redesCarregadas = [];
        }
      }
      setRedesWifi(redesCarregadas);

      // `base` preserva o name vazio da copia: nao devolver o name aqui e o que
      // impede a copia de virar PUT na politica de origem.
      setOriginal({ ...base, sitesBloqueios: sitesCarregados, redesWifi: redesCarregadas, aplicativos: apps });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível carregar a política', {
        variant: 'error',
      });
    } finally {
      setCarregando(false);
    }
  }, [name, copiarDe, ehCopia, enqueueSnackbar]);

  React.useEffect(() => {
    carregar();
  }, [carregar]);

  /* CATALOGO PAGINADO, SO NA LUPA (21/10/2026). O modal baixava o catalogo
     inteiro (rows=5000) ao abrir qualquer politica. Agora pede 20 por pagina ao
     mesmo endpoint (status=1), so quando se clica na lupa. Os que ja estao na
     politica aparecem avisados, e `adicionarAplicativos` os ignora. */
  const pacotesNaPolitica = React.useMemo(() => new Set(aplicativos.map((a) => a.packageName)), [aplicativos]);
  const buscarAplicativos = React.useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/aplicativos/paginado?page=${pagina}&rows=${linhas}&status=1&valor=${encodeURIComponent(termo)}` +
          `&campo=${termo.includes('.') ? 'packageName' : 'name'}&direcao=asc&ordenar=name`,
        (a) => ({
          codigo: a.packageName,
          rotulo: a.name || a.packageName,
          secundario: pacotesNaPolitica.has(a.packageName) ? `${a.packageName} · já está nesta política` : a.packageName,
          dado: a,
        })
      ),
    [pacotesNaPolitica]
  );

  // ------------------------------------------------------------- aplicativos
  function adicionarAplicativos() {
    if (selecionados.length === 0 || !tipoNovo) {
      enqueueSnackbar('Informe o aplicativo e o tipo de instalação', { variant: 'warning' });
      return;
    }

    // Os novos entram na FRENTE da lista, como na tela antiga: quem acabou de
    // adicionar precisa ver o que adicionou sem rolar a tabela.
    const novos: AplicativoPolitica[] = selecionados
      .filter((a) => !pacotesNaPolitica.has(a.packageName))
      .map((a) => ({
      packageName: a.packageName,
      codigo: a.codigo,
      name: a.name,
      installType: tipoNovo,
      autoUpdateMode: atualizacaoNova,
      novo: true,
    }));
    const lista = [...novos, ...aplicativos];

    setAplicativos(lista);
    recalcularListaKiosk(lista);
    setSelecionados([]);
    setTipoNovo('FORCE_INSTALLED');
    setAtualizacaoNova('AUTO_UPDATE_DEFAULT');
  }

  function removerAplicativo(item: AplicativoPolitica) {
    const lista = aplicativos.filter((a) => a.packageName !== item.packageName);
    setAplicativos(lista);
    recalcularListaKiosk(lista);

    if (item.packageName === kiosk.valor) setKiosk(ITEM_PADRAO_KIOSK);
    setEditandoApp(null);
    enqueueSnackbar(`(${item.name}) foi removido da lista.`, { variant: 'info' });
  }

  function salvarAplicativo(item: AplicativoPolitica) {
    // Sobrescreve SO installType e autoUpdateMode: `codigo` e `novo` continuam,
    // senao o aplicativo recem-adicionado deixaria de contar como novo no histórico.
    const lista = aplicativos.map((a) =>
      a.packageName === item.packageName
        ? { ...a, installType: item.installType, autoUpdateMode: item.autoUpdateMode }
        : a
    );
    setAplicativos(lista);
    recalcularListaKiosk(lista);
    setEditandoApp(null);
    enqueueSnackbar('Aplicativo alterado na lista.', { variant: 'info' });
  }

  /**
   * Trocar o app de quiosque. Duas consequencias, ambas da tela antiga:
   * o app que SAI volta para FORCE_INSTALLED, e o que ENTRA vai para
   * REQUIRED_FOR_SETUP (menos o GPShub padrao, que nao esta na lista de apps).
   */
  function trocarKiosk(pacote: string) {
    const novo = listaKiosk.find((k) => k.valor === pacote) || ITEM_PADRAO_KIOSK;
    let lista = [...aplicativos];

    if (kiosk.valor !== novo.valor) {
      lista = lista.map((a) => (a.packageName === kiosk.valor ? { ...a, installType: 'FORCE_INSTALLED' } : a));
    }
    if (novo.valor !== KIOSK_PADRAO_PACKAGE) {
      lista = lista.map((a) => (a.packageName === novo.valor ? { ...a, installType: 'REQUIRED_FOR_SETUP' } : a));
    }

    setAplicativos(lista);
    setKiosk(novo);
  }

  const vinculadosFiltrados = React.useMemo(() => {
    if (!buscaVinculados.trim()) return aplicativos;
    const alvo = buscaVinculados.trim().toLowerCase();
    // String(...) de proposito: installType/autoUpdateMode podem vir nulos, e
    // `null.toLowerCase()` derrubava o filtro da tela antiga.
    return aplicativos.filter((a) => String((a as any)[campoFiltro] ?? '').toLowerCase().includes(alvo));
  }, [aplicativos, buscaVinculados, campoFiltro]);

  /** Exporta os aplicativos VINCULADOS - a tela antiga exportava os disponiveis. */
  async function exportarVinculados() {
    if (!vinculadosFiltrados.length) return;
    setExportando(true);
    try {
      const { baixarPlanilha } = await import('../../utils/planilha');
      await baixarPlanilha(
        vinculadosFiltrados.map((a) => ({
          Nome: a.name || '',
          Pacote: a.packageName || '',
          Tipo: rotuloTipo(a.installType),
          'Atualização': rotuloAtualizacao(a.autoUpdateMode),
        })),
        `aplicativos-politica-${form.nome || name}`
      );
    } finally {
      setExportando(false);
    }
  }

  // -------------------------------------------------------------------- sites
  function adicionarSite() {
    const lista = [...sites];

    if (siteTodos === 'Não') {
      if (!siteTexto.trim()) {
        enqueueSnackbar('Coloque o site', { variant: 'warning' });
        return;
      }
      if (lista.some((s) => String(s.site || '').toLowerCase() === siteTexto.trim().toLowerCase())) {
        enqueueSnackbar('Já existe esse site na lista', { variant: 'warning' });
        return;
      }
      lista.push({
        novo: true,
        codigo: Math.floor(Math.random() * 999999),
        todos: 'Não',
        site: siteTexto.trim(),
        tipo: siteTipo,
      });
    } else {
      if (lista.some((s) => s.todos === 'Sim')) {
        enqueueSnackbar('Só é possível ter um TODOS na lista', { variant: 'warning' });
        return;
      }
      lista.push({
        novo: true,
        codigo: Math.floor(Math.random() * 999999),
        todos: 'Sim',
        site: '',
        tipo: siteTipo,
      });
    }

    setSites(lista);
    setSiteTipo('Bloqueio');
    setSiteTodos('Não');
    setSiteTexto('');
  }

  function removerSite(linha: Site) {
    if (!linha?.codigo) return;
    setSites(sites.filter((s) => s.codigo !== linha.codigo));
  }

  // ------------------------------------------------------------------- salvar
  /**
   * Diferencas para o `dataCampo`, que e o unico insumo do historico: o backend
   * (RegisterHistorico) le esta lista e nada mais. Campo fora daqui muda no
   * aparelho sem deixar rastro - foi o que acontecia com dataRoamingDisabled.
   */
  function montarDiferencas(): HistoricoCampoDataProps[] {
    const lista: HistoricoCampoDataProps[] = [];
    const marca = (v: any) => (v ? 'Marcado' : 'Não Marcado');

    const booleano = (chave: string, rotulo: string) => {
      if (!!original[chave] !== !!form[chave]) {
        lista.push({ campo: rotulo, antigo: marca(original[chave]), novo: marca(form[chave]) });
      }
    };
    const texto = (chave: string, rotulo: string) => {
      if (String(original[chave] ?? '') !== String(form[chave] ?? '')) {
        lista.push({ campo: rotulo, antigo: String(original[chave] ?? ' - '), novo: String(form[chave] ?? ' - ') });
      }
    };

    texto('nome', 'Nome');

    booleano('smsDisabled', 'Sms');
    booleano('addUserDisabled', 'Novos usuarios no perfil');
    booleano('uninstallAppsDisabled', 'Desistalar aplicativos');
    booleano('bluetoothConfigDisabled', 'Configuração de Bluetooth');
    booleano('screenCaptureDisabled', 'Captura de tela');
    booleano('adjustVolumeDisabled', 'Sem som (bloqueia o volume)');
    booleano('wifiConfigDisabled', 'Wifi');
    booleano('networkResetDisabled', 'Restauração de rede');
    booleano('factoryResetDisabled', 'Restauração de padrão de Fábrica');
    booleano('installAppsDisabled', 'Instalar aplicativos pelo usuario');
    booleano('modifyAccountsDisabled', 'Modificar conta');
    booleano('mountPhysicalMediaDisabled', 'Está ativando mídia externa física');
    booleano('bluetoothContactSharingDisabled', 'Compartilhar contatos via Bluetooth');
    booleano('credentialsConfigDisabled', 'Indica se a configuração de credenciais de usuário');
    booleano('mobileNetworksConfigDisabled', 'Configuração de redes móveis está desativada');
    // Tinha checkbox e NAO tinha comparacao: mudar so este e salvar caia no gate
    // "Nao houve alteracoes" e a alteracao era descartada em silencio.
    booleano('dataRoamingDisabled', 'Redes móveis (roaming)');
    booleano('powerButtonActions', 'Botão power');
    booleano('vpnConfigDisabled', 'Configuração da VPN está desativada.');
    booleano('createWindowsDisabled', 'Criação de janelas além das janelas do aplicativo está desativada');
    booleano('outgoingBeamDisabled', 'NFC para enviar dados de aplicativos');
    booleano('outgoingCallsDisabled', 'Se as chamadas de saída estão desativadas');
    booleano('removeUserDisabled', 'remoção de outros usuários está desativada');
    booleano('cpfObrigatorio', 'CPF obrigatorio');
    booleano('crObrigatorio', 'CR obrigatorio');
    booleano('deviceSettings', 'Acesso Configurações');

    // bluetoothDisabled/bloqueioSenha/configTela sao 0|1 na tela e booleano no
    // banco; comparamos pela verdade dos dois lados.
    if (!!original.bluetoothDisabled !== !!form.bluetoothDisabled) {
      lista.push({
        campo: 'Bluetooth (Ligar/Desligar)',
        antigo: original.bluetoothDisabled ? 'DESATIVADO' : 'ATIVADO',
        novo: form.bluetoothDisabled ? 'DESATIVADO' : 'ATIVADO',
      });
    }
    if (!!original.bloqueioSenha !== !!form.bloqueioSenha) {
      lista.push({
        campo: 'Bloqueio de Senha',
        antigo: marca(original.bloqueioSenha),
        novo: marca(form.bloqueioSenha),
      });
    }
    if (!!original.configTela !== !!form.configTela) {
      lista.push({ campo: 'Configuração de tela', antigo: marca(original.configTela), novo: marca(form.configTela) });
    }
    if (Number(original.status) !== Number(form.status)) {
      lista.push({
        campo: 'Status',
        antigo: Number(original.status) === 1 ? 'ATIVO' : 'INATIVO',
        novo: Number(form.status) === 1 ? 'ATIVO' : 'INATIVO',
      });
    }

    texto('playStoreMode', 'PlayStore Modo');
    texto('autoDateAndTimeZone', 'Fuso horário');
    texto('untrustedAppsPolicy', 'Fonte desconhecida');
    texto('developerSettings', 'Opção de desenvolvedor');
    texto('cameraAccess', 'Camera');
    texto('systemUpdate', 'Atualização de sistema');
    texto('locationMode', 'Localização');
    texto('airplaneModeState', 'Modo avião');
    texto('tetheringSettings', 'Compartilhamento Wi-Fi, tethering Bluetooth');
    texto('usbDataAccess', 'USB - Transferencia/USO');
    texto('crossProfileCopyPaste', 'Copia/Cola Texto');
    texto('crossProfileDataSharing', 'Compartilhar Documentos');
    texto('frpAdminEmails', 'Lista e-mail');
    // A tela antiga gravava o valor NOVO nos dois lados deste campo.
    texto('hub', 'GPS Hub(Tipo instalação)');

    if (kiosk.valor !== original.kiosk) {
      lista.push({ campo: 'Modo Kiosk', antigo: String(original.kiosk ?? ' - '), novo: String(kiosk.valor) });
    }

    if (Number(original.barraStatus ?? -1) !== Number(form.barraStatus)) {
      const rotulo = (v: any) => OPCOES.barraStatus.find((o) => Number(o.valor) === Number(v))?.rotulo || ' - ';
      lista.push({
        campo: 'Navegação e Status bar',
        antigo: original.barraStatus == null ? ' - ' : rotulo(original.barraStatus),
        novo: rotulo(form.barraStatus),
      });
    }

    // Aplicativos: alteracao de tipo, de atualizacao, remocao e inclusao.
    (original.aplicativos || []).forEach((antigo: any) => {
      const atual = aplicativos.find((a) => a.packageName === antigo.packageName);
      if (atual && atual.installType !== antigo.installType) {
        lista.push({ campo: `Aplicativos(${antigo.name})`, antigo: antigo.installType, novo: atual.installType });
      }
      if (atual && atual.autoUpdateMode !== antigo.autoUpdateMode) {
        lista.push({ campo: `Aplicativos(${antigo.name})`, antigo: antigo.autoUpdateMode, novo: atual.autoUpdateMode });
      }
      // Remover da lista e mudanca SEMPRE. Condicionado a `uninstallAppsDisabled`,
      // a remocao sumia do diff e o salvar dizia "Nao houve alteracoes na politica".
      if (!atual) {
        lista.push({ campo: `Aplicativos(${antigo.name})`, antigo: antigo.name, novo: 'removido' });
      }
    });
    aplicativos.forEach((a) => {
      if (a.novo) {
        lista.push({
          campo: 'Aplicativos(novo)',
          antigo: ' - ',
          novo: `Tipo: ${a.installType}, Pacote: ${a.packageName}`,
        });
      }
    });

    // Redes Wi-Fi (sem senha no historico)
    (original.redesWifi || []).forEach((antiga: RedeWifi) => {
      const atual = redesWifi.find((r) => r.codigo === antiga.codigo);
      if (!atual) lista.push({ campo: `wifi(${antiga.ssid})`, antigo: antiga.ssid, novo: 'removida' });
      else if (atual.ssid !== antiga.ssid || atual.seguranca !== antiga.seguranca || atual.oculta !== antiga.oculta || atual.senha)
        lista.push({ campo: `wifi(${antiga.ssid})`, antigo: `${antiga.ssid} (${antiga.seguranca})`, novo: `${atual.ssid} (${atual.seguranca})${atual.senha ? ', senha alterada' : ''}` });
    });
    redesWifi
      .filter((r) => r.codigo === undefined)
      .forEach((r) => lista.push({ campo: 'wifi(nova)', antigo: ' - ', novo: `${r.ssid} (${r.seguranca})` }));

    // Sites
    (original.sitesBloqueios || []).forEach((antigo: Site) => {
      if (!sites.some((s) => s.codigo === antigo.codigo)) {
        lista.push({ campo: `sites(${antigo.site})`, antigo: String(antigo.tipo), novo: 'removido' });
      }
    });
    sites.forEach((s) => {
      if (s.novo) {
        lista.push({
          campo: 'sites(novo)',
          antigo: ' - ',
          novo: `Site: ${s.site}, Tipo: ${s.tipo}, TODOS: ${s.todos}`,
        });
      }
    });

    return lista;
  }

  function montarCorpo(diferencas: HistoricoCampoDataProps[]) {
    return {
      // Vazio na criacao e na copia: e por este campo que o backend decide entre
      // criar e atualizar.
      name: original.name ? original.name : undefined,
      entidade: form.entidade || null,
      nome: form.nome,
      regional: form.entidade?.regional,
      playStoreMode: form.playStoreMode,
      bluetoothConfigDisabled: form.bluetoothConfigDisabled,
      untrustedAppsPolicy: form.untrustedAppsPolicy,
      cameraAccess: form.cameraAccess,
      kiosk: kiosk?.valor ?? KIOSK_PADRAO_PACKAGE,
      smsDisabled: form.smsDisabled,
      screenCaptureDisabled: form.screenCaptureDisabled,
      locationMode: form.locationMode,
      tetheringSettings: form.tetheringSettings,
      frpAdminEmails: form.frpAdminEmails,
      adjustVolumeDisabled: form.adjustVolumeDisabled,
      bluetoothDisabled: Number(form.bluetoothDisabled) === 1,
      wifiConfigDisabled: form.wifiConfigDisabled,
      systemUpdate: form.systemUpdate,
      networkResetDisabled: form.networkResetDisabled,
      dataRoamingDisabled: form.dataRoamingDisabled,
      usbDataAccess: form.usbDataAccess,
      bloqueioSenha: Number(form.bloqueioSenha) === 1,
      configTela: Number(form.configTela) === 1,
      crObrigatorio: form.crObrigatorio,
      cpfObrigatorio: form.cpfObrigatorio,
      configRoteador: form.configRoteador,
      factoryResetDisabled: form.factoryResetDisabled,
      hub: form.hub,
      airplaneModeState: form.airplaneModeState,
      defaultPermissionPolicy: form.defaultPermissionPolicy,
      installAppsDisabled: form.installAppsDisabled,
      modifyAccountsDisabled: form.modifyAccountsDisabled,
      uninstallAppsDisabled: form.uninstallAppsDisabled,
      addUserDisabled: form.addUserDisabled,
      mountPhysicalMediaDisabled: form.mountPhysicalMediaDisabled,
      bluetoothContactSharingDisabled: form.bluetoothContactSharingDisabled,
      credentialsConfigDisabled: form.credentialsConfigDisabled,
      mobileNetworksConfigDisabled: form.mobileNetworksConfigDisabled,
      vpnConfigDisabled: form.vpnConfigDisabled,
      developerSettings: form.developerSettings,
      createWindowsDisabled: form.createWindowsDisabled,
      outgoingBeamDisabled: form.outgoingBeamDisabled,
      outgoingCallsDisabled: form.outgoingCallsDisabled,
      removeUserDisabled: form.removeUserDisabled,
      autoDateAndTimeZone: form.autoDateAndTimeZone,
      crossProfileCopyPaste: form.crossProfileCopyPaste ?? 'CROSS_PROFILE_COPY_PASTE_ALLOWED',
      crossProfileDataSharing: form.crossProfileDataSharing ?? 'CROSS_PROFILE_DATA_SHARING_ALLOWED',
      status: form.status,
      powerButtonActions: form.powerButtonActions,
      barraStatus: form.barraStatus,
      deviceSettings: form.deviceSettings,
      dataCampo: original.name ? JSON.stringify(diferencas) : null,
      sitesBloqueios: sites,
      redesWifi: redesWifi.map((r) => ({
        codigo: r.codigo,
        ssid: r.ssid.trim(),
        seguranca: r.seguranca,
        senha: r.senha,
        oculta: r.oculta,
      })),
      applications: aplicativos.map((a) => ({
        packageName: a.packageName,
        installType: a.installType,
        autoUpdateMode: a.autoUpdateMode,
      })),
    };
  }

  async function salvar() {
    if (!String(form.nome || '').trim()) {
      enqueueSnackbar('Nome obrigatório', { variant: 'warning' });
      return;
    }
    if (String(form.nome).length > 150) {
      enqueueSnackbar('O tamanho máximo do nome é 150', { variant: 'warning' });
      return;
    }
    if (!form.entidade?.codigo) {
      enqueueSnackbar('Selecione o grupo', { variant: 'warning' });
      return;
    }

    // Aviso da tela antiga (era um Swal): as duas opcoes NAO agem sobre o que ja
    // esta instalado, e a expectativa contraria e comum.
    if ((form.uninstallAppsDisabled || form.installAppsDisabled) && !confirmarApps) {
      setConfirmarApps(true);
      return;
    }
    setConfirmarApps(false);

    const diferencas = montarDiferencas();

    // O gate de "sem alteracoes" vale SO na edicao. Na tela antiga ele rodava
    // tambem na criacao, onde a lista e sempre vazia - e por isso criar e duplicar
    // politica nunca funcionaram.
    if (original.name && diferencas.length === 0) {
      enqueueSnackbar('Não houve alterações na politica.', { variant: 'info' });
      return;
    }

    setSalvando(true);
    try {
      const corpo = montarCorpo(diferencas);
      const resposta = original.name
        ? await axios.put(`/politicas/${encodeURIComponent(original.name)}`, corpo)
        : await axios.post('/politicas', corpo);

      enqueueSnackbar('Salvo com sucesso', { variant: 'success' });

      // Sem navegacao: quem abriu decide o que fazer. Na criacao devolve o `name`
      // gerado pelo backend, para a lista poder reabrir a politica ja salva.
      if (!original.name && resposta.data?.name) {
        onSalvo?.(resposta.data.name);
      } else {
        await carregar();
        onSalvo?.(original.name);
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Ops erro ao cadastrar', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  }

  // ---------------------------------------------------------------------- UI
  const titulo = ehCopia ? `Nova política (cópia de ${copiarDe})` : editando ? form.nome || name : 'Nova política';

  /* Aviso de perfil e aviso de copia vao no PRIMEIRO painel, nao acima das abas:
     no modal o espaco vertical e o recurso escasso, e um bloco fixo no topo
     roubaria altura de todos os blocos para dizer algo que so importa uma vez. */
  const avisos = (
    <>
      {!podeSalvar && (
        <div className="rounded-lg border border-warn bg-warn-soft p-3 text-[12px]">
          Seu perfil pode consultar a política, mas não salvar alterações.
        </div>
      )}

      {ehCopia && (
        <div className="rounded-lg border border-info bg-info-soft p-3 text-[12px]">
          Você está criando uma <strong>nova política</strong> a partir de <strong>{copiarDe}</strong>. Os campos, os
          aplicativos e os sites bloqueados foram copiados; o nome recebeu o prefixo &quot;Copia de&quot; e o token de
          enrollment é gerado ao salvar. A política de origem não é alterada. <strong>Escolha o grupo</strong> da
          cópia: precisa ser um grupo que ainda não tenha política (cada grupo tem uma só).
        </div>
      )}
    </>
  );

  if (!aberto) return null;

  if (carregando) {
    return (
      <div className="fixed inset-0 z-[1400] grid place-items-center bg-black/40 p-4 backdrop-blur-[2px]">
        <div className="rounded-lg border border-line bg-surface p-8 text-center text-[12.5px] text-muted-foreground shadow-card">
          Carregando política...
        </div>
      </div>
    );
  }

  /* VALIDACAO POR ABA - o mesmo gate que o `salvar()` aplica, quebrado por bloco
     para o ✓ da aba significar algo. Identificacao e o unico bloco com campo
     obrigatorio (nome e grupo); os demais nao tem como estar invalidos, entao ficam
     validos. Os dois que sao ferramenta e nao cadastro ficam `opcional`, para nao
     ganharem ✓ verde sem validar nada. */
  const identificacaoValida = Boolean(String(form.nome || '').trim() && form.entidade?.codigo);

  const passos: PassoModal[] = [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      grupo: 'Cadastro',
      valido: identificacaoValida,
      pendencia: 'nome e grupo',
      conteudo: (
        <>
          {avisos}

              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Campo
                    rotulo="Nome"
                    obrigatorio
                    dica={original.nome ? 'O nome não muda depois de criada.' : 'Até 150 caracteres.'}
                  >
                    <input
                      className={classesEntrada}
                      value={form.nome || ''}
                      maxLength={150}
                      disabled={Boolean(original.nome)}
                      onChange={(e) => set('nome', e.target.value)}
                    />
                  </Campo>
                </div>

                {original.url && (
                  <div className="md:col-span-2">
                    <Campo rotulo="URL de enrollment">
                      <div className="break-all rounded-lg border border-line bg-soft px-3 py-2 tabular-nums text-[12px]">
                        {original.url}
                      </div>
                    </Campo>
                  </div>
                )}

                <SeletorGrupo
                  valor={form.entidade}
                  somente={original.entidade?.codigo}
                  desabilitado={Boolean(original.entidade?.codigo)}
                  onChange={(g) => set('entidade', g)}
                />

                <Campo rotulo="Regional" dica="Vem do grupo; não é escolhida aqui.">
                  <div className="flex h-9 items-center rounded-lg border border-line bg-soft px-3 text-[12.5px] text-muted-foreground">
                    {form.entidade?.regional?.nome || 'Selecione o Grupo'}
                  </div>
                </Campo>

                <Selecao
                  rotulo="Status"
                  valor={form.status}
                  opcoes={OPCOES.status}
                  onChange={(v) => set('status', v)}
                />

                {/* VOLTA PARA O GRUPO (29/09/2026).

                    O link ja existia, mas como texto sublinhado espremido numa
                    linha de campos - passava despercebido. Agora e um cartao,
                    igual ao "Política do grupo" do outro lado: grupo e politica
                    sao um par, e quem abre um quase sempre precisa conferir o
                    outro. Os dois caminhos com o mesmo peso.

                    `?editar=<codigo>` e tratado por entidades-novo (linha ~90),
                    e abre o modal do grupo direto - conferido. */}
                {original.entidade?.codigo && (
                  <div className="flex items-end">
                    <NextLink
                      href={`/dashboard/entidades-novo?editar=${original.entidade.codigo}`}
                      className="flex w-full items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 p-3 hover:border-primary"
                    >
                      <span className="min-w-0">
                        <span className="block text-[12px] font-bold">Grupo desta política</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {original.entidade.nome}
                        </span>
                      </span>
                      <span aria-hidden className="flex-none text-[13px] text-primary">→</span>
                    </NextLink>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <div className="mb-1.5 text-[12px] font-semibold">
                  Campos obrigatórios no registro do dispositivo
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Caixa
                    rotulo="CPF"
                    dica="Exige CPF ao registrar o aparelho."
                    marcado={form.cpfObrigatorio}
                    onChange={(v) => set('cpfObrigatorio', v)}
                  />
                  <Caixa
                    rotulo="CR"
                    dica="Exige CR ao registrar o aparelho."
                    marcado={form.crObrigatorio}
                    onChange={(v) => set('crObrigatorio', v)}
                  />
                </div>
              </div>
        </>
      ),
    },
    {
      id: 'aplicativos',
      titulo: 'Aplicativos',
      grupo: 'Cadastro',
      valido: true,
      conteudo: (
        <>
          <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={exportarVinculados}
                  disabled={exportando || !vinculadosFiltrados.length}
                  className="h-8 rounded-lg border border-line px-2.5 text-[12px] font-semibold text-muted-foreground disabled:opacity-40"
                  title="Baixa em .xlsx os aplicativos vinculados a esta política"
                >
                  {exportando ? 'Exportando...' : '↓ .xlsx'}
                </button>
              </div>

              <div className="rounded-lg border border-line p-3">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="md:col-span-3">
                    <Campo bloco rotulo="Adicionar aplicativos" dica="Marque no seletor da lupa; as fichas são removíveis.">
                      <AutoCompleteMultiplo
                        selecionados={selecionados.map((a) => ({ codigo: a.packageName, rotulo: a.name || a.packageName }))}
                        aoMudar={(l) => setSelecionados(l.map((o) => o.dado ?? selecionados.find((s) => s.packageName === o.codigo)))}
                        buscar={buscarAplicativos}
                        titulo="Selecionar aplicativos"
                        placeholder="Nome ou pacote (Enter ou lupa)"
                        vazio="Nenhum aplicativo ativo encontrado."
                      />
                    </Campo>
                  </div>

                  <Selecao
                    rotulo="Tipo de instalação"
                    dica={DESCRICAO_TIPO[tipoNovo]}
                    valor={tipoNovo}
                    opcoes={TIPOS_INSTALACAO.map((t) => ({ valor: t.valor, rotulo: t.rotulo }))}
                    onChange={setTipoNovo}
                  />
                  <Selecao
                    rotulo="Atualização"
                    valor={atualizacaoNova}
                    opcoes={MODOS_ATUALIZACAO.map((m) => ({ valor: m.valor, rotulo: m.rotulo }))}
                    onChange={setAtualizacaoNova}
                  />
                  <div className="flex items-end">
                    <BotaoPrimario onClick={adicionarAplicativos} disabled={selecionados.length === 0}>
                      Adicionar {selecionados.length > 0 ? `(${selecionados.length})` : ''}
                    </BotaoPrimario>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="mb-2 flex flex-wrap items-end gap-2">
                  <div className="w-44">
                    <Campo rotulo="Filtrar por">
                      <select
                        className={classesEntrada}
                        value={campoFiltro}
                        onChange={(e) => setCampoFiltro(e.target.value)}
                      >
                        <option value="name">Nome</option>
                        <option value="packageName">Pacote</option>
                        <option value="installType">Tipo</option>
                        <option value="autoUpdateMode">Atualização</option>
                      </select>
                    </Campo>
                  </div>
                  <div className="min-w-[200px] flex-1">
                    <Campo rotulo="Pesquisar nos vinculados">
                      <input
                        className={classesEntrada}
                        placeholder="Pesquisar aplicativo"
                        value={buscaVinculados}
                        onChange={(e) => setBuscaVinculados(e.target.value)}
                      />
                    </Campo>
                  </div>
                </div>

                {/* Exporta os aplicativos VINCULADOS a esta politica, respeitando o
                    filtro digitado. E a lista que alguem precisa conferir com outra
                    area ("estes sao os apps obrigatorios do grupo X"), e ate agora
                    so dava para ler na tela ou tirar print. */}
                <div className="mb-2 flex items-center justify-end">
                  <BotaoExportar
                    className="h-7 px-2 text-[11px]"
                    rotulo={`Excel (${vinculadosFiltrados.length})`}
                    nomeArquivo={`politica-aplicativos-${name || 'nova'}`}
                    linhas={() =>
                      vinculadosFiltrados.map((a, i) => ({
                        '#': i + 1,
                        Nome: a.name || '',
                        Tipo: rotuloTipo(a.installType),
                        Pacote: a.packageName || '',
                        'Atualização': rotuloAtualizacao(a.autoUpdateMode),
                      }))
                    }
                  />
                </div>

                <div className="max-h-[420px] overflow-auto rounded-lg border border-line">
                  <table className="w-full border-collapse text-[12px]">
                    <thead className="sticky top-0 bg-soft">
                      <tr>
                        <th className="px-3 py-2 text-left font-bold">#</th>
                        <th className="px-3 py-2 text-left font-bold">Nome</th>
                        <th className="px-3 py-2 text-left font-bold">Tipo</th>
                        <th className="px-3 py-2 text-left font-bold">Pacote</th>
                        <th className="px-3 py-2 text-left font-bold">Atualização</th>
                        <th className="px-3 py-2 text-right font-bold">Opções</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vinculadosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-faint">
                            Nenhum aplicativo nesta política.
                          </td>
                        </tr>
                      )}
                      {vinculadosFiltrados.map((a, i) => (
                        <tr key={a.packageName} className="border-t border-line hover:bg-soft">
                          <td className="px-3 py-1.5">
                            <Num className="text-faint">{i + 1}</Num>
                          </td>
                          <td className="px-3 py-1.5 font-semibold">
                            {a.name}
                            {a.novo && (
                              <span className="ml-2 rounded bg-ok-soft px-1.5 py-0.5 text-[10px] font-bold text-ok">
                                NOVO
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5">{rotuloTipo(a.installType)}</td>
                          <td className="px-3 py-1.5">
                            <Num className="text-[11px] text-muted-foreground">{a.packageName}</Num>
                          </td>
                          <td className="px-3 py-1.5">{rotuloAtualizacao(a.autoUpdateMode)}</td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              type="button"
                              onClick={() => setEditandoApp(a)}
                              className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:text-ink"
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
        </>
      ),
    },
    {
      id: 'kiosk',
      titulo: 'Modo Kiosk',
      grupo: 'Configuração',
      valido: true,
      conteudo: (
        <>
              <div className="grid gap-3 md:grid-cols-3">
                <Selecao
                  rotulo="Aplicativo do Kiosk"
                  dica={DICAS.kiosk}
                  valor={kiosk.valor}
                  opcoes={listaKiosk}
                  onChange={(v) => trocarKiosk(String(v))}
                />
                <Selecao rotulo="Status do Kiosk" dica={DICAS.hub} valor={form.hub} opcoes={OPCOES.hub} onChange={(v) => set('hub', v)} />
                <Selecao
                  rotulo="Navegação e Notificação"
                  dica={DICAS.barraStatus}
                  valor={form.barraStatus}
                  opcoes={OPCOES.barraStatus}
                  onChange={(v) => set('barraStatus', v)}
                />
              </div>

              <div className="mt-4">
                <div className="mb-1.5 text-[12px] font-semibold">Bloqueios quando o Kiosk está bloqueado</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Caixa
                    rotulo="Configurações"
                    dica="Configurações do dispositivo."
                    marcado={form.deviceSettings}
                    onChange={(v) => set('deviceSettings', v)}
                  />
                  <Caixa
                    rotulo="Botão power"
                    marcado={form.powerButtonActions}
                    onChange={(v) => set('powerButtonActions', v)}
                  />
                </div>
              </div>
        </>
      ),
    },
    {
      id: 'bloqueios',
      titulo: 'Outros bloqueios',
      grupo: 'Configuração',
      valido: true,
      conteudo: (
        <>
              <div className="grid gap-3 md:grid-cols-2">
                <Selecao rotulo="PlayStore Modo" dica={DICAS.playStoreMode} valor={form.playStoreMode} opcoes={OPCOES.playStoreMode} onChange={(v) => set('playStoreMode', v)} />
                <Selecao rotulo="Modo Avião" dica={DICAS.airplaneModeState} valor={form.airplaneModeState} opcoes={OPCOES.airplaneModeState} onChange={(v) => set('airplaneModeState', v)} />
                <Selecao rotulo="Localização" dica={DICAS.locationMode} valor={form.locationMode} opcoes={OPCOES.locationMode} onChange={(v) => set('locationMode', v)} />
                <Selecao rotulo="Câmera" dica={DICAS.cameraAccess} valor={form.cameraAccess} opcoes={OPCOES.cameraAccess} onChange={(v) => set('cameraAccess', v)} />
                <Selecao rotulo="Fontes desconhecidas" dica={DICAS.untrustedAppsPolicy} valor={form.untrustedAppsPolicy} opcoes={OPCOES.untrustedAppsPolicy} onChange={(v) => set('untrustedAppsPolicy', v)} />
                <Selecao rotulo="Roteador Wifi e Bluetooth (tethering)" dica={DICAS.tetheringSettings} valor={form.tetheringSettings} opcoes={OPCOES.tetheringSettings} onChange={(v) => set('tetheringSettings', v)} />
                <Selecao rotulo="Bluetooth (Ligar/Desligar)" dica={DICAS.bluetoothDisabled} valor={form.bluetoothDisabled} opcoes={OPCOES.bluetoothDisabled} onChange={(v) => set('bluetoothDisabled', v)} />
                <Selecao rotulo="Atualização de sistema" dica={DICAS.systemUpdate} valor={form.systemUpdate} opcoes={OPCOES.systemUpdate} onChange={(v) => set('systemUpdate', v)} />
                <Selecao rotulo="Opções do desenvolvedor" dica={DICAS.developerSettings} valor={form.developerSettings} opcoes={OPCOES.developerSettings} onChange={(v) => set('developerSettings', v)} />
                <Selecao rotulo="USB: dados e mouse/teclado" dica={DICAS.usbDataAccess} valor={form.usbDataAccess} opcoes={OPCOES.usbDataAccess} onChange={(v) => set('usbDataAccess', v)} />
                <Selecao rotulo="Copia/Cola texto entre perfis" dica={DICAS.crossProfileCopyPaste} valor={form.crossProfileCopyPaste} opcoes={OPCOES.crossProfileCopyPaste} onChange={(v) => set('crossProfileCopyPaste', v)} />
                <Selecao rotulo="Compartilhar documentos entre perfis" dica={DICAS.crossProfileDataSharing} valor={form.crossProfileDataSharing} opcoes={OPCOES.crossProfileDataSharing} onChange={(v) => set('crossProfileDataSharing', v)} />
                <Selecao rotulo="Bloqueio de tela com senha" dica={DICAS.bloqueioSenha} valor={form.bloqueioSenha} opcoes={OPCOES.bloqueioSenha} onChange={(v) => set('bloqueioSenha', v)} />
                <Selecao rotulo="Fuso horário / hora" dica={DICAS.autoDateAndTimeZone} valor={form.autoDateAndTimeZone} opcoes={OPCOES.autoDateAndTimeZone} onChange={(v) => set('autoDateAndTimeZone', v)} />
                <Selecao rotulo="Configuração de tela" dica={DICAS.configTela} valor={form.configTela} opcoes={OPCOES.configTela} onChange={(v) => set('configTela', v)} />
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <Campo
                  rotulo="E-mails dos administradores"
                  dica="Somente e-mails com domínio Gmail, separados por ';'."
                >
                  <input
                    className={classesEntrada}
                    value={form.frpAdminEmails || ''}
                    onChange={(e) => set('frpAdminEmails', e.target.value)}
                  />
                </Campo>
                <div className="flex items-end">
                  <div className="w-full">
                    <Caixa
                      rotulo="Desabilitar restauração de fábrica"
                      dica="Inativa a opção de restauração de fábrica no aparelho."
                      marcado={form.factoryResetDisabled}
                      onChange={(v) => set('factoryResetDisabled', v)}
                    />
                  </div>
                </div>
              </div>
        </>
      ),
    },
    {
      id: 'sites',
      titulo: 'Sites bloqueados',
      grupo: 'Configuração',
      valido: true,
      conteudo: (
        <>
              <div className="grid items-end gap-3 md:grid-cols-4">
                {/* A DICA DIZIA O CONTRARIO DO QUE ACONTECE (29/09/2026).

                    Dizia "Sim aplica a regra a todos os sites, EXCETO OS
                    LISTADOS". Em `createPolicy.ts` (~619), uma linha com
                    Todos=Sim e tipo Bloqueio faz `URLBlocklist = []` e empurra
                    `*` - ou seja, APAGA os sites bloqueados que estavam na
                    lista, em vez de excetua-los.

                    Quem montasse "bloqueia tudo, exceto estes" com as excecoes
                    marcadas como Bloqueio perdia a lista inteira e bloqueava
                    tudo - o oposto do que configurou.

                    O codigo nao esta errado, o texto e que estava: a excecao
                    existe, mas se escreve como LIBERADO, que vira URLAllowlist e
                    tem precedencia sobre a lista de bloqueio no Chrome. E isso
                    que a dica passa a dizer. */}
                <Campo rotulo="Todos" dica="Sim aplica a regra a TODOS os sites e descarta os desta mesma lista. Para abrir exceções, cadastre-as com tipo Liberado.">
                  <select className={classesEntrada} value={siteTodos} onChange={(e) => setSiteTodos(e.target.value)}>
                    <option value="Não">Não</option>
                    <option value="Sim">Sim</option>
                  </select>
                </Campo>

                <Campo rotulo="Site" dica="Exemplo: https://www.youtube.com">
                  <input
                    className={classesEntrada}
                    placeholder="Site para bloquear ou liberar"
                    disabled={siteTodos === 'Sim'}
                    value={siteTexto}
                    onChange={(e) => setSiteTexto(e.target.value)}
                  />
                </Campo>

                <Campo rotulo="Tipo">
                  <select className={classesEntrada} value={siteTipo} onChange={(e) => setSiteTipo(e.target.value)}>
                    <option value="Bloqueio">Bloqueio</option>
                    <option value="Liberado">Liberado</option>
                  </select>
                </Campo>

                <BotaoPrimario onClick={adicionarSite}>Adicionar</BotaoPrimario>
              </div>

                              <div className="mb-2 flex items-center justify-end">
                  {/* Sites bloqueados/liberados da politica - mesma razao dos
                      aplicativos: e lista que se confere fora do sistema. */}
                  <BotaoExportar
                    className="h-7 px-2 text-[11px]"
                    rotulo={`Excel (${sites.length})`}
                    nomeArquivo={`politica-sites-${name || 'nova'}`}
                    linhas={() =>
                      sites.map((st) => ({
                        'Código': st.codigo ?? '',
                        /* 🔴 Era `st.todos ? 'Sim' : 'Não'` - uma checagem de
                           VERDADE sobre uma STRING. A coluna TODOS guarda o texto
                           'Sim'/'Não' (ver politicas.sites.entity: `todos: string`),
                           e a string 'Não' e truthy em JavaScript: a planilha saia
                           com "Sim" em TODA linha, inclusive nas que aplicam a um
                           site especifico. A tabela na tela mostra `s.todos` cru e
                           estava certa - so a exportacao mentia.
                           Compara por String() e aceita '1'/'true' porque a base
                           tem linha antiga gravada quando o insert usava `? 1 : 0` -
                           o tipo declarado e string, mas o dado real varia. */
                        Todos: ['Sim', '1', 'true'].includes(String(st.todos)) ? 'Sim' : 'Não',
                        Site: st.site || '',
                        Tipo: st.tipo || '',
                      }))
                    }
                  />
                </div>
<div className="mt-4 overflow-x-auto rounded-lg border border-line">
                <table className="w-full border-collapse text-[12px]">
                  <thead className="bg-soft">
                    <tr>
                      <th className="px-3 py-2 text-left font-bold">Código</th>
                      <th className="px-3 py-2 text-left font-bold">Todos</th>
                      <th className="px-3 py-2 text-left font-bold">Site</th>
                      <th className="px-3 py-2 text-left font-bold">Tipo</th>
                      <th className="px-3 py-2 text-right font-bold">Opções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sites.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-faint">
                          Nenhum site na lista.
                        </td>
                      </tr>
                    )}
                    {sites.map((s) => (
                      <tr key={s.codigo} className="border-t border-line hover:bg-soft">
                        <td className="px-3 py-1.5">
                          <Num className="text-faint">{s.codigo}</Num>
                        </td>
                        <td className="px-3 py-1.5">{s.todos}</td>
                        <td className="px-3 py-1.5 font-semibold">{s.site || '(todos)'}</td>
                        <td className="px-3 py-1.5">{s.tipo}</td>
                        <td className="px-3 py-1.5 text-right">
                          <button
                            type="button"
                            onClick={() => removerSite(s)}
                            className="rounded-md border border-danger px-2 py-1 text-[11px] font-semibold text-danger"
                          >
                            Remover
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
        </>
      ),
    },
    {
      id: 'wifi',
      titulo: 'Redes Wi-Fi',
      grupo: 'Configuração',
      opcional: true,
      valido: redesWifi.every(redeWifiValida),
      resumo: redesWifi.length ? `${redesWifi.length} rede(s)` : 'nenhuma',
      conteudo: (
        <>
          <div className="rounded-lg border border-info bg-info-soft p-3 text-[12px]">
            Redes que os aparelhos desta política <strong>já recebem configuradas</strong>, sem ninguém digitar a senha.
            Elas <strong>somam</strong> às redes que o aparelho já tem — nenhuma rede cadastrada pelo usuário é apagada
            ou bloqueada. Tirar uma rede daqui remove só ela dos aparelhos. Se o aparelho já tiver uma rede com o mesmo
            nome, passa a valer a configuração daqui.
          </div>

          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full min-w-[640px] text-[12px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Nome da rede (SSID)</th>
                  <th className="px-3 py-2">Segurança</th>
                  <th className="px-3 py-2">Senha</th>
                  <th className="px-3 py-2">Oculta</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {redesWifi.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-faint">
                      Nenhuma rede extra. Os aparelhos usam só as redes que já têm.
                    </td>
                  </tr>
                )}
                {redesWifi.map((r) => {
                  const muda = (campo: Partial<RedeWifi>) =>
                    setRedesWifi((lista) => lista.map((x) => (x.chave === r.chave ? { ...x, ...campo } : x)));
                  const invalida = !redeWifiValida(r);
                  return (
                    <tr key={r.chave} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <input
                          value={r.ssid}
                          maxLength={32}
                          onChange={(e) => muda({ ssid: e.target.value })}
                          placeholder="GPS-Obra"
                          className={classesEntrada}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={r.seguranca}
                          onChange={(e) => muda({ seguranca: e.target.value as RedeWifi['seguranca'], senha: '' })}
                          className={classesEntrada}
                        >
                          <option value="WPA-PSK">WPA/WPA2 (com senha)</option>
                          <option value="WEP-PSK">WEP (antiga)</option>
                          <option value="None">Aberta (sem senha)</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {r.seguranca === 'None' ? (
                          <span className="text-faint">—</span>
                        ) : (
                          <input
                            type="password"
                            autoComplete="new-password"
                            value={r.senha}
                            onChange={(e) => muda({ senha: e.target.value })}
                            placeholder={r.temSenha ? '•••••• (mantida)' : r.seguranca === 'WPA-PSK' ? '8 a 63 caracteres' : '5 ou 13 caracteres'}
                            className={classesEntrada}
                          />
                        )}
                        {invalida && r.ssid.trim() && (
                          <span className="mt-1 block text-[11px] text-danger">
                            {r.seguranca === 'WPA-PSK' ? 'Senha de 8 a 63 caracteres.' : 'Senha WEP de 5 ou 13 caracteres.'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 pt-4">
                        <input type="checkbox" checked={r.oculta} onChange={(e) => muda({ oculta: e.target.checked })} />
                      </td>
                      <td className="px-3 py-2 pt-3 text-right">
                        <button
                          type="button"
                          onClick={() => setRedesWifi((lista) => lista.filter((x) => x.chave !== r.chave))}
                          className="text-[12px] font-semibold text-danger hover:underline"
                        >
                          remover
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <BotaoSecundario
            disabled={redesWifi.length >= MAX_REDES_WIFI}
            onClick={() =>
              setRedesWifi((lista) => [
                ...lista,
                { chave: `n${Date.now()}`, ssid: '', seguranca: 'WPA-PSK', senha: '', oculta: false },
              ])
            }
          >
            + Adicionar rede{redesWifi.length >= MAX_REDES_WIFI ? ` (máximo ${MAX_REDES_WIFI})` : ''}
          </BotaoSecundario>
        </>
      ),
    },
    {
      id: 'padroes',
      titulo: 'Configurações padrão',
      grupo: 'Configuração',
      valido: true,
      conteudo: (
        <>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {CAIXAS_PADRAO.map((c) => (
                  <Caixa
                    key={c.chave}
                    rotulo={c.rotulo}
                    dica={c.dica}
                    marcado={form[c.chave]}
                    onChange={(v) => set(c.chave, v)}
                  />
                ))}
              </div>
        </>
      ),
    },
    /* A ABA "Enviados sem controle" SAIU (19/08/2026, a pedido).
       Ela listava, em leitura, as 11 chaves que a politica carrega e reenvia sem
       ter controle na tela. A intencao era nao esconder campo que vai para o
       aparelho - mas numa tela de cadastro isso e uma aba de diagnostico, e quem
       edita politica nao esta diagnosticando. A informacao nao se perdeu: a lista
       continua em `SEM_CONTROLE`, no topo deste arquivo, e e ela que alimenta o
       `montarCorpo` - so nao e mais desenhada. */
    // O historico so aparece na EDICAO: em politica nova nao existe chave
    // para consultar, e uma aba vazia e pior que aba ausente.
    ...((editando && original.name
      ? [
          {
            id: 'historico',
            titulo: 'Histórico',
            grupo: 'Ferramentas',
            informativo: true,
            conteudo: (
              <>
                <HistoricoGPS tipo={'POLITICA'} chave={original.name} />
              </>
            ),
          },
        ]
      : []) as PassoModal[]),
  ];

  return (
    <>
      <ModalPassos
        aberto={aberto}
        titulo={titulo}
        subtitulo={editando ? name : undefined}
        passos={passos}
        rotuloFinalizar="Salvar"
        salvando={salvando}
        onFinalizar={salvar}
        onFechar={onFechar}
      />

      <ModalAplicativoPolitica
        aberto={Boolean(editandoApp)}
        item={editandoApp}
        onFechar={() => setEditandoApp(null)}
        onSalvar={salvarAplicativo}
        onRemover={removerAplicativo}
      />

      <Modal
        aberto={confirmarApps}
        titulo="Confirme antes de salvar"
        descricao="Duas opções marcadas não fazem o que o nome sugere."
        onFechar={() => setConfirmarApps(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmarApps(false)}>Não</BotaoSecundario>
            <BotaoPrimario onClick={salvar}>Sim, continuar</BotaoPrimario>
          </>
        }
      >
        <div className="rounded-lg border border-warn bg-warn-soft p-3 text-[12px]">
          <p className="font-semibold text-ink">
            Você marcou {form.uninstallAppsDisabled && <strong>Desistalar aplicativos</strong>}
            {form.uninstallAppsDisabled && form.installAppsDisabled && ' e '}
            {form.installAppsDisabled && <strong>Adicionar aplicativos</strong>}.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-muted-foreground">
            <li>
              <strong>Desistalar aplicativos</strong> não remove nada do aparelho: apenas impede que o usuário
              desinstale.
            </li>
            <li>
              <strong>Adicionar aplicativos</strong> não instala nada: apenas impede que o usuário instale.
            </li>
          </ul>
        </div>
      </Modal>
    </>
  );
}
